import { useState, useEffect, useCallback } from 'react';
import supabase, { orgId } from '../supabaseClient';
import { NM } from '../utils/constants';

const KEY = 'overhead_categories';

const DEFAULT_CATEGORIES = [
  {
    id: 'marketing', name: 'Marketing',
    subs: [
      { id: 'mkt_spend', name: 'Marketing Spend' },
      { id: 'mkt_events', name: 'Events' },
      { id: 'mkt_other', name: 'Other' },
    ],
  },
  {
    id: 'premises', name: 'Premises',
    subs: [
      { id: 'pre_rent', name: 'Rent' },
      { id: 'pre_other', name: 'Other Property Costs' },
    ],
  },
  {
    id: 'technology', name: 'Technology',
    subs: [
      { id: 'tech_software', name: 'Software Costs' },
      { id: 'tech_other', name: 'Other Costs' },
    ],
  },
  {
    id: 'legal', name: 'Legal & Professional',
    subs: [
      { id: 'leg_accounting', name: 'Accounting' },
      { id: 'leg_legal', name: 'Legal' },
      { id: 'leg_recruitment', name: 'Recruitment' },
      { id: 'leg_insurance', name: 'Insurance' },
      { id: 'leg_other', name: 'Other Prof Fees' },
    ],
  },
  {
    id: 'general', name: 'Other',
    subs: [
      { id: 'gen_travel', name: 'Travel' },
      { id: 'gen_entertaining', name: 'Entertaining' },
      { id: 'gen_bank', name: 'Bank Fees' },
      { id: 'gen_other', name: 'Other Expenses' },
    ],
  },
  {
    id: 'below_ebitda', name: 'Below EBITDA', belowEbitda: true,
    subs: [
      { id: 'be_depreciation',    name: 'Depreciation' },
      { id: 'be_amortisation',    name: 'Amortisation' },
      { id: 'be_interest_rec',    name: 'Interest Receivable' },
      { id: 'be_interest_pay',    name: 'Interest Payable' },
      { id: 'be_forex',           name: 'Forex' },
      { id: 'be_exceptional',     name: 'Exceptional Items' },
      { id: 'be_recharges',       name: 'Recharges' },
      { id: 'corp_tax',           name: 'Corporation Tax' },
    ],
  },
];

// Values stored as: { [subId]: Array(NM) of strings }
const VALUES_KEY = 'overhead_values';

function makeId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function useOverheadCategories() {
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [values, setValues]         = useState({});
  const [loaded, setLoaded]         = useState(false);

  useEffect(() => {
    async function load() {
      const { data: catData } = await supabase.from('app_settings')
        .select('value').eq('org_id', orgId).eq('key', KEY).maybeSingle();
      if (catData?.value) {
        try { setCategories(JSON.parse(catData.value)); } catch (_) {}
      }
      const { data: valData } = await supabase.from('app_settings')
        .select('value').eq('org_id', orgId).eq('key', VALUES_KEY).maybeSingle();
      if (valData?.value) {
        try { setValues(JSON.parse(valData.value)); } catch (_) {}
      }
      setLoaded(true);
    }
    load();
  }, []);

  const saveCategories = useCallback(async (next) => {
    setCategories(next);
    await supabase.from('app_settings').upsert(
      { org_id: orgId, key: KEY, value: JSON.stringify(next) },
      { onConflict: 'org_id,key' }
    );
  }, []);

  const saveValues = useCallback(async (next) => {
    setValues(next);
    await supabase.from('app_settings').upsert(
      { org_id: orgId, key: VALUES_KEY, value: JSON.stringify(next) },
      { onConflict: 'org_id,key' }
    );
  }, []);

  // Category CRUD
  const addCategory = (name) => saveCategories([...categories, { id: makeId(), name, subs: [] }]);
  const updateCategory = (id, name) => saveCategories(categories.map(c => c.id === id ? { ...c, name } : c));
  const deleteCategory = (id) => {
    if (!window.confirm('Delete this category and all its sub-categories?')) return;
    saveCategories(categories.filter(c => c.id !== id));
  };

  // Sub-category CRUD
  const addSub = (catId, name) => saveCategories(categories.map(c =>
    c.id === catId ? { ...c, subs: [...c.subs, { id: makeId(), name }] } : c
  ));
  const updateSub = (catId, subId, name) => saveCategories(categories.map(c =>
    c.id === catId ? { ...c, subs: c.subs.map(s => s.id === subId ? { ...s, name } : s) } : c
  ));
  const deleteSub = (catId, subId) => saveCategories(categories.map(c =>
    c.id === catId ? { ...c, subs: c.subs.filter(s => s.id !== subId) } : c
  ));

  // Value editing
  const updateValue = (subId, monthIdx, val) => {
    const next = {
      ...values,
      [subId]: (() => {
        const arr = [...(values[subId] || Array(NM).fill(''))];
        arr[monthIdx] = val;
        return arr;
      })(),
    };
    saveValues(next);
  };

  // Monthly totals by category and overall
  const getCategoryMonthlyTotal = useCallback((catId, plActuals, plMappings, startDate, currentMonthIdx) => {
    const cat = categories.find(c => c.id === catId);
    if (!cat) return Array(NM).fill(0);
    return Array.from({ length: NM }, (_, i) => {
      const isLocked = currentMonthIdx !== null && currentMonthIdx !== undefined && i <= currentMonthIdx;
      if (isLocked && startDate && plActuals && plMappings) {
        const totalMonths = startDate.month + i;
        const yr = startDate.year + Math.floor(totalMonths / 12);
        const mo = ((totalMonths % 12) + 12) % 12;
        const monthKey = `${yr}-${String(mo + 1).padStart(2, '0')}`;
        const monthActuals = plActuals[monthKey] || {};
        let total = 0;
        cat.subs.forEach(sub => {
          Object.entries(monthActuals).forEach(([accName, val]) => {
            const mapping = plMappings[accName];
            if (mapping?.targetId === sub.id && (mapping?.type === 'overhead_sub' || mapping?.type === 'below_ebitda_sub')) {
              total += parseFloat(val) || 0;
            }
          });
        });
        return total; // returns 0 if nothing mapped — correct behaviour
      }
      return cat.subs.reduce((s, sub) => s + (parseFloat(values[sub.id]?.[i]) || 0), 0);
    });
  }, [categories, values]);

  const getAllMonthlyTotal = useCallback(() =>
    Array.from({ length: NM }, (_, i) =>
      categories.reduce((s, cat) =>
        s + cat.subs.reduce((ss, sub) => ss + (parseFloat(values[sub.id]?.[i]) || 0), 0), 0)
    )
  , [categories, values]);

  return {
    categories, values, loaded,
    saveCategories,
    addCategory, updateCategory, deleteCategory,
    addSub, updateSub, deleteSub,
    updateValue,
    getCategoryMonthlyTotal,
    getAllMonthlyTotal,
  };
}
